import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import type { TDocumentDefinitions, Content, TableCell } from 'pdfmake/interfaces';
import { ReportExportTable } from './report-export-rows.service';

export interface ReportPdfExportOptions {
  title: string;
  subtitle?: string;
  filename: string;
  table?: ReportExportTable;
  content?: Content[];
  pageOrientation?: 'portrait' | 'landscape';
  /** Override default page margins [left, top, right, bottom] in PDF points. */
  pageMargins?: [number, number, number, number];
  /** When false, omit footer page numbers (e.g. Statistics presentation PDFs). Default true. */
  showPageNumbers?: boolean;
}

interface PdfMakeCreatedPdf {
  getBlob(callback: (blob: Blob) => void): void;
}

interface PdfMakeInstance {
  createPdf(doc: TDocumentDefinitions): PdfMakeCreatedPdf;
  vfs?: unknown;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
}

interface FileSystemWritableFileStream {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}

export interface PdfExportFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

export interface SavePdfBlobOptions {
  /** Skip File System Access API and use a normal browser download (Chrome download shelf). */
  preferBrowserDownload?: boolean;
}

type ShowSaveFilePicker = (options?: SaveFilePickerOptions) => Promise<PdfExportFileHandle>;

@Injectable({
  providedIn: 'root',
})
export class ReportPdfExportService {
  private pdfMakePromise: Promise<PdfMakeInstance> | null = null;

  /**
   * Build pdfmake document definition (exposed for unit tests).
   */
  buildDocDefinition(options: ReportPdfExportOptions): TDocumentDefinitions {
    const { title, subtitle, table, content, pageOrientation, pageMargins, showPageNumbers = true } = options;

    const docContent: Content[] = [{ text: title, style: 'title' }];
    if (subtitle) {
      docContent.push({ text: subtitle, style: 'subtitle', margin: [0, 0, 0, 8] });
    }

    if (content?.length) {
      docContent.push(...content);
    } else if (table) {
      const headerRow: TableCell[] = table.headers.map((h) => ({
        text: h,
        style: 'tableHeader',
      }));
      const bodyRows: TableCell[][] = table.rows.map((row) =>
        row.map((cell) => ({ text: cell ?? '', style: 'tableCell' }))
      );
      docContent.push({
        table: {
          headerRows: 1,
          widths: this.columnWidths(table.headers.length),
          body: [headerRow, ...bodyRows],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? '#f0f0f0' : null),
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cccccc',
          vLineColor: () => '#cccccc',
        },
      });
    }

    const resolvedOrientation =
      pageOrientation ?? (table && table.headers.length > 5 ? 'landscape' : 'portrait');

    const docDefinition: TDocumentDefinitions = {
      pageSize: 'A4',
      pageOrientation: resolvedOrientation,
      pageMargins: pageMargins ?? [40, 48, 40, 48],
      content: docContent,
      styles: {
        title: { fontSize: 14, bold: true, margin: [0, 0, 0, 4] },
        subtitle: { fontSize: 9, color: '#444444' },
        sectionTitle: { fontSize: 10, bold: true, margin: [0, 8, 0, 4] },
        filterLine: { fontSize: 9, margin: [0, 2, 0, 0] },
        kpiLine: { fontSize: 9, margin: [0, 2, 0, 0] },
        widgetTitle: { fontSize: 11, bold: true, margin: [0, 0, 0, 8] },
        tableHeader: { fontSize: 8, bold: true },
        tableCell: { fontSize: 8 },
        alertTaskId: { fontSize: 7, color: '#666666' },
        detailLabel: { fontSize: 8, bold: true },
        detailSection: { fontSize: 8, bold: true },
        detailText: { fontSize: 8 },
        detailCompact: { fontSize: 7, lineHeight: 1.15 },
        detailColumnHeader: { fontSize: 7, bold: true, fillColor: '#f5f5f5' },
        detailNotesBody: { fontSize: 8, lineHeight: 1.25 },
        detailEmphasis: { fontSize: 8, bold: true },
      },
      defaultStyle: {
        font: 'Roboto',
      },
    };

    if (showPageNumbers) {
      docDefinition.footer = (currentPage, pageCount) => ({
        text: `${currentPage} / ${pageCount}`,
        alignment: 'center',
        fontSize: 8,
        margin: [0, 8, 0, 0],
      });
    }

    return docDefinition;
  }

  async exportTable(
    options: ReportPdfExportOptions,
    fileHandle?: PdfExportFileHandle
  ): Promise<void> {
    const filename = this.normalizeFilename(options.filename);
    const blob = await this.generateBlob(options);
    await this.savePdfBlob(blob, filename, fileHandle);
  }

  /** Whether the browser supports the native Save As dialog. */
  isSaveLocationPickerSupported(): boolean {
    return this.canUseFileSystemAccess();
  }

  /**
   * Prompt for save location while the user activation from the click is still valid.
   * Call this before long-running export work (e.g. widget capture).
   */
  async promptSaveFile(filename: string): Promise<PdfExportFileHandle | undefined> {
    if (!this.canUseFileSystemAccess()) {
      return undefined;
    }

    const showSaveFilePicker = (window as Window & { showSaveFilePicker?: ShowSaveFilePicker })
      .showSaveFilePicker;
    if (!showSaveFilePicker) {
      return undefined;
    }

    try {
      return await showSaveFilePicker({
        suggestedName: this.normalizeFilename(filename),
        types: [
          {
            description: 'PDF',
            accept: { 'application/pdf': ['.pdf'] },
          },
        ],
      });
    } catch (error) {
      const err = error as DOMException;
      if (err?.name === 'AbortError') {
        throw new Error('PDF export cancelled');
      }
      throw error;
    }
  }

  async generateBlob(options: ReportPdfExportOptions): Promise<Blob> {
    const pdfMake = await this.loadPdfMake();
    const docDefinition = this.buildDocDefinition(options);

    return new Promise<Blob>((resolve, reject) => {
      try {
        pdfMake.createPdf(docDefinition).getBlob((result: Blob) => {
          if (!result || result.size === 0) {
            reject(new Error('PDF generation produced an empty file'));
            return;
          }
          resolve(result);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /** Save a PDF blob using a pre-selected handle or native picker / download fallback. */
  async savePdfBlob(
    blob: Blob,
    filename: string,
    fileHandle?: PdfExportFileHandle,
    options?: SavePdfBlobOptions
  ): Promise<void> {
    const normalizedFilename = this.normalizeFilename(filename);

    if (options?.preferBrowserDownload) {
      this.saveFile(blob, normalizedFilename);
      return;
    }

    if (fileHandle) {
      const writable = await fileHandle.createWritable();
      try {
        await writable.write(blob);
        await writable.close();
      } catch (error) {
        try {
          await writable.abort?.();
        } catch {
          // ignore abort failures
        }
        throw error;
      }
      return;
    }

    if (this.canUseFileSystemAccess()) {
      try {
        const handle = await this.promptSaveFile(normalizedFilename);
        if (handle) {
          await this.savePdfBlob(blob, normalizedFilename, handle);
          return;
        }
      } catch (error) {
        const err = error as DOMException & Error;
        if (err?.name === 'AbortError' || err?.message === 'PDF export cancelled') {
          throw new Error('PDF export cancelled');
        }
        this.saveFile(blob, normalizedFilename);
        return;
      }
    }

    this.saveFile(blob, normalizedFilename);
  }

  private normalizeFilename(filename: string): string {
    const sanitized = filename.replace(/[/\\?%*:|"<>]/g, '-');
    return sanitized.endsWith('.pdf') ? sanitized : `${sanitized}.pdf`;
  }

  protected saveFile(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  private canUseFileSystemAccess(): boolean {
    if (typeof window === 'undefined' || !('showSaveFilePicker' in window)) {
      return false;
    }
    try {
      return window.self === window.top;
    } catch {
      return false;
    }
  }

  private columnWidths(columnCount: number): (string | number)[] {
    if (columnCount <= 0) {
      return ['*'];
    }
    return Array(columnCount).fill('*');
  }

  private async loadPdfMake(): Promise<PdfMakeInstance> {
    if (!this.pdfMakePromise) {
      this.pdfMakePromise = this.initializePdfMake().catch((error) => {
        this.pdfMakePromise = null;
        throw error;
      });
    }
    return this.pdfMakePromise;
  }

  private async initializePdfMake(): Promise<PdfMakeInstance> {
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    const pdfMake = ((pdfMakeModule as { default?: PdfMakeInstance }).default ??
      pdfMakeModule) as PdfMakeInstance;
    const vfs = this.resolvePdfMakeVfs(pdfFontsModule);
    if (!vfs) {
      throw new Error('pdfmake fonts not loaded');
    }
    pdfMake.vfs = vfs;
    return pdfMake;
  }

  /** Supports both ESM default and namespace interop shapes from vfs_fonts. */
  private resolvePdfMakeVfs(pdfFontsModule: unknown): unknown | undefined {
    const mod = pdfFontsModule as {
      default?: { pdfMake?: { vfs?: unknown } };
      pdfMake?: { vfs?: unknown };
    };
    return mod.default?.pdfMake?.vfs ?? mod.pdfMake?.vfs;
  }
}
